import type { AxiosInstance, AxiosRequestConfig, Method } from 'axios';

import { apiClient, publicApiClient } from '@/api/client';
import { normalizeListResponse } from '@/api/listResponse';

type AuthMode = 'authenticated' | 'public';

type ApiRequestOptions<TBody = unknown, TParams = unknown> = {
  url: string;
  method?: Method;
  data?: TBody;
  params?: TParams;
  headers?: AxiosRequestConfig['headers'];
  timeout?: number;
  authMode?: AuthMode;
};

function resolveClient(authMode: AuthMode): AxiosInstance {
  return authMode === 'public' ? publicApiClient : apiClient;
}

export async function requestData<TResponse, TBody = unknown, TParams = unknown>({
  url,
  method = 'get',
  data,
  params,
  headers,
  timeout,
  authMode = 'authenticated',
}: ApiRequestOptions<TBody, TParams>): Promise<TResponse> {
  const client = resolveClient(authMode);
  const response = await client.request<TResponse>({
    url,
    method,
    data,
    params,
    headers,
    timeout,
  });

  return response.data;
}

export async function requestList<TItem, TBody = unknown, TParams = unknown>(
  options: ApiRequestOptions<TBody, TParams>,
): Promise<TItem[]> {
  const data = await requestData<TItem[] | { results: TItem[] }, TBody, TParams>(options);
  return normalizeListResponse<TItem>(data);
}
